import React from 'react';
import { useFilePreview } from '../hooks/useFilePreview';
import { getFileType, formatFileSize } from '../util/fileUtils';
import PDFPreview from './previews/PDFPreview';
import ImagePreview from './previews/ImagePreview';
import SpreadsheetPreview from './previews/SpreadsheetPreview';
import TextPreview from './previews/TextPreview';
import DocumentPreview from './previews/DocumentPreview';
import UnsupportedPreview from './previews/UnsupportedPreview';

const AttachmentPreviewModal = ({ isOpen, onClose, attachment }) => {
  const { loading, error, previewData, retry } = useFilePreview(attachment, isOpen);

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="bg-gray-100 rounded-lg p-8 text-center min-h-96 flex items-center justify-center">
          <div className="text-gray-500">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg">Loading preview...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 rounded-lg p-8 text-center min-h-96 flex items-center justify-center">
          <div className="text-red-500">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-lg font-medium">Preview Error</p>
            <p className="text-sm mt-2">{error}</p>
            <button 
              onClick={retry}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (!previewData) return null;

    const props = {
      filename: attachment.name,
      url: previewData.url,
      content: previewData.content
    };

    switch (previewData.type) {
      case 'pdf':
        return <PDFPreview {...props} />;
      case 'image':
        return <ImagePreview {...props} />;
      case 'spreadsheet':
        return <SpreadsheetPreview {...props} />;
      case 'text':
      case 'code':
        return <TextPreview {...props} />;
      case 'document':
        return <DocumentPreview {...props} />;
      default:
        return <UnsupportedPreview filename={attachment.name} fileType={previewData.type} />;
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = attachment.url || `/api/files/${attachment.id}`;
    link.download = attachment.name;
    link.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" 
          onClick={onClose}
        ></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {attachment?.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {formatFileSize(attachment?.size)} • {getFileType(attachment?.name || '').toUpperCase()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {renderPreview()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;